import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { type UserSchema, type UserFormErrors, validateUser, EMPTY_USER } from '@klear/ui/lib/user-form-schema'
import { useTraders } from '@klear/ui/hooks/use-traders'
import UserFormCommon from '@/app/user/UserFormCommon'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { ROUTE_USER } from '@klear/constants'
import { useUserMutations } from '@klear/ui/hooks/use-user'
import { userSchemaToCreateWire } from '@klear/ui/lib/user-wire'
import isError from 'lodash/isError'
import isFinite from 'lodash/isFinite'
import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import { Button } from '@klear/ui/components/button'
import { DELETED_USER_EMAIL_MESSAGE } from '@klear/user/wire.client'

const UserAdd: React.FC = () => {
  useDocumentTitle('New User')
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()

  const [values, setValues] = useState<UserSchema>(EMPTY_USER)
  const [errors, setErrors] = useState<UserFormErrors>({})
  const [submitting, setSubmitting] = useState(false)

  const { traders, isLoading: loadingTraders, error: tradersError } = useTraders()
  const { createUser, restoreUser, isRestoring } = useUserMutations()
  const [deletedEmail, setDeletedEmail] = useState<string | null>(null)

  const onChange = (patch: Partial<UserSchema>) => setValues((prev) => ({ ...prev, ...patch }))

  if (tradersError) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h2 className="text-xl font-semibold text-red-600">Error loading traders</h2>
        <p className="text-muted-foreground">{tradersError.message}</p>
      </div>
    )
  }

  const onSubmit = async () => {
    const nextErrors: UserFormErrors = validateUser(values)

    const role = values.roles?.[0]
    if (!role?.role) {
      nextErrors.roles = 'User must have one role'
    } else if (role.traderId == null) {
      nextErrors.roles = 'Please select a trader'
    } else if (!isFinite(toNumber(role.traderId))) {
      nextErrors.roles = 'Invalid trader selection'
    }

    setErrors(nextErrors)
    if (!isEmpty(nextErrors)) return

    setSubmitting(true)
    try {
      await createUser(userSchemaToCreateWire(values))
      navigate(`/${ROUTE_USER}`)
    } catch (error) {
      console.error('Error creating user:', error)
      setDeletedEmail(isError(error) && error.message === DELETED_USER_EMAIL_MESSAGE ? values.email : null)
      setErrors({
        root: isError(error) ? error.message : 'Failed to create user. Please try again.'
      })
    } finally {
      setSubmitting(false)
    }
  }

  const onRestore = async () => {
    if (!deletedEmail) return
    try {
      const restored = await restoreUser({ email: deletedEmail })
      navigate(restored?.id ? `/${ROUTE_USER}/${restored.id}` : `/${ROUTE_USER}`)
    } catch (error) {
      setDeletedEmail(null)
      setErrors({ root: isError(error) ? error.message : 'Failed to restore user. Please try again.' })
    }
  }

  return (
    <UserFormCommon
      values={values}
      errors={errors}
      onChange={onChange}
      isNewUser={true}
      traders={traders}
      onSubmit={onSubmit}
      cancelPath={`/${ROUTE_USER}`}
      title="Create New User"
      loadingTraders={loadingTraders}
      submitting={submitting}
      currentUserIsSuperAdmin={currentUser?.isSuperAdmin}
      rootAction={
        deletedEmail && (
          <Button type="button" variant="outline" disabled={isRestoring} onClick={() => void onRestore()}>
            Restore user
          </Button>
        )
      }
    />
  )
}

export default UserAdd
